import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import type { PaymentInstructionsSettings, ReferralInvoice } from "@shared/schema";
import { referralInvoicePartyLabel } from "@shared/schema";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { AlertTriangle, BellRing, CheckCircle2, Eye, FileText, Loader2, Send, XCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { INVOICE_STATUS_COLORS, INVOICE_STATUS_LABELS } from "./constants";

export function ReferralCommissionsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [markPaidInvoice, setMarkPaidInvoice] = useState<ReferralInvoice | null>(null);
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [partyFilter, setPartyFilter] = useState<"all" | "Lender" | "Broker" | "Realtor">("all");

  const invoicesQuery = useQuery<ReferralInvoice[]>({ queryKey: ["/api/admin/referrals/invoices"] });

  const paymentInstructionsQuery = useQuery<PaymentInstructionsSettings | null>({
    queryKey: ["/api/admin/settings/payment-instructions"],
    queryFn: async () => {
      const res = await fetch("/api/admin/settings/payment-instructions", { credentials: "include" });
      return res.json();
    },
  });

  // Mirrors server getGlobalPaymentInstructionsText() logic: any non-empty field = configured
  const paymentInstructionsConfigured = !!(paymentInstructionsQuery.data && (
    paymentInstructionsQuery.data.preferredMethod ||
    paymentInstructionsQuery.data.bankName ||
    paymentInstructionsQuery.data.accountName ||
    paymentInstructionsQuery.data.accountNumber ||
    paymentInstructionsQuery.data.routingNumber ||
    paymentInstructionsQuery.data.wireInstructions ||
    paymentInstructionsQuery.data.achMemoFormat ||
    paymentInstructionsQuery.data.zelleInfo ||
    paymentInstructionsQuery.data.notes
  ));

  const voidMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("PATCH", `/api/admin/referrals/invoices/${id}`, { status: "voided" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/invoices"] });
      toast({ title: "Invoice voided" });
    },
    onError: (err: unknown) =>
      toast({
        title: "Failed to void invoice",
        description: parseErrorMessage(err, "Couldn't void this invoice. Please try again."),
        variant: "destructive",
      }),
  });

  const approveMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("PATCH", `/api/admin/referrals/invoices/${id}`, { status: "sent" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/invoices"] });
      toast({ title: "Dispute cleared", description: "Invoice marked as sent and outstanding." });
    },
    onError: (err: unknown) =>
      toast({
        title: "Failed to clear dispute",
        description: parseErrorMessage(err, "Couldn't clear this dispute. Please try again."),
        variant: "destructive",
      }),
  });

  const sendMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("POST", `/api/admin/referrals/invoices/${id}/send`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/invoices"] });
      toast({ title: "Invoice sent" });
    },
    onError: (err: unknown) =>
      toast({
        title: "Failed to send invoice",
        description: parseErrorMessage(err, "Couldn't send this invoice. Please try again."),
        variant: "destructive",
      }),
  });

  const markPaidMutation = useMutation({
    mutationFn: async ({ id, paymentDate }: { id: number; paymentDate: string }) =>
      apiRequest("PATCH", `/api/admin/referrals/invoices/${id}`, { status: "paid", paymentDate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/invoices"] });
      toast({ title: "Invoice marked paid" });
      setMarkPaidInvoice(null);
    },
    onError: (err: unknown) =>
      toast({
        title: "Failed to mark paid",
        description: parseErrorMessage(err, "Couldn't mark this invoice paid. Please try again."),
        variant: "destructive",
      }),
  });

  const openMarkPaid = (inv: ReferralInvoice) => {
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setMarkPaidInvoice(inv);
  };

  const reminderMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/admin/referrals/invoices/${id}/send-reminder`);
      return (await res.json()) as { message: string; reminderCount?: number };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals/invoices"] });
      toast({ title: "Reminder sent", description: data.message });
    },
    onError: (err: unknown) => {
      toast({
        title: "Failed to send reminder",
        description: parseErrorMessage(err, "Could not send reminder."),
        variant: "destructive",
      });
    },
  });


  const invoices = invoicesQuery.data ?? [];
  const filteredInvoices = partyFilter === "all"
    ? invoices
    : invoices.filter(i => referralInvoicePartyLabel(i) === partyFilter);
  const totalPaid = invoices.filter(i => i.status === "paid").reduce((sum, i) => sum + parseFloat(i.amount as unknown as string || "0"), 0);
  const totalOutstanding = invoices.filter(i => ["pending_payment_instructions", "sent", "overdue", "disputed"].includes(i.status ?? "")).reduce((sum, i) => sum + parseFloat(i.amount as unknown as string || "0"), 0);
  const overdueCount = invoices.filter(i => i.status === "overdue").length;
  const partyFilters: Array<"all" | "Lender" | "Broker" | "Realtor"> = ["all", "Lender", "Broker", "Realtor"];

  return (
    <div className="space-y-6">
      {/* Payment Instructions Warning */}
      {!paymentInstructionsQuery.isLoading && !paymentInstructionsConfigured && (
        <div className="flex items-start gap-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-4 py-3" data-testid="banner-referral-payment-instructions-missing">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-amber-800 dark:text-amber-200">Payment instructions not configured</p>
            <p className="text-amber-700 dark:text-amber-300 mt-0.5">Invoice send buttons are disabled until payment instructions are set up. Go to <strong>Settings → Payment Instructions</strong> to configure them.</p>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="text-xs text-gray-500 mb-1">Total Paid</div>
          <div className="text-xl font-bold text-emerald-600">${totalPaid.toLocaleString("en-US", { minimumFractionDigits: 2 })}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-gray-500 mb-1">Outstanding</div>
          <div className="text-xl font-bold text-blue-600" data-testid="text-total-outstanding">${totalOutstanding.toLocaleString("en-US", { minimumFractionDigits: 2 })}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-gray-500 mb-1">Overdue Invoices</div>
          <div className="text-xl font-bold text-red-600">{overdueCount}</div>
        </Card>
      </div>

      {/* Invoice table */}
      <Card>
        <div className="p-4 border-b flex flex-wrap items-center gap-2">
          <FileText className="h-4 w-4 text-brand" />
          <h3 className="font-semibold text-sm">Referral Invoices</h3>
          <Badge variant="secondary" className="text-xs">{filteredInvoices.length}</Badge>
          <div className="ml-auto flex items-center gap-1" data-testid="filter-invoice-party">
            {partyFilters.map(option => (
              <Button
                key={option}
                size="sm"
                variant={partyFilter === option ? "default" : "outline"}
                className="h-7 text-xs"
                onClick={() => setPartyFilter(option)}
                data-testid={`button-filter-party-${option.toLowerCase()}`}
              >
                {option === "all" ? "All" : option}
              </Button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          {invoicesQuery.isLoading ? (
            <div className="p-4 space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : invoices.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No invoices generated yet. They auto-generate when a registered lender or broker is matched to a property going Active.</div>
          ) : filteredInvoices.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500" data-testid="text-no-filtered-invoices">No {partyFilter} invoices.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Invoice #</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Recipient</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Company</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Property</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Amount</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Due Date</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Reminders</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredInvoices.map(inv => (
                  <tr key={inv.id} data-testid={`row-invoice-${inv.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">{inv.invoiceNumber}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium" data-testid={`text-recipient-name-${inv.id}`}>{inv.lenderContactName}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid={`badge-invoice-party-${inv.id}`}>
                          {referralInvoicePartyLabel(inv)}
                        </Badge>
                      </div>
                      <div className="text-xs text-gray-500" data-testid={`text-recipient-email-${inv.id}`}>{inv.lenderEmail}</div>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-700" data-testid={`text-lender-company-${inv.id}`}>
                      {inv.lenderCompanyName || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600 max-w-[160px] truncate">{inv.propertyAddress || `#${inv.propertyId}`}</td>
                    <td className="px-4 py-2 font-medium">${parseFloat(inv.amount as unknown as string || "0").toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${INVOICE_STATUS_COLORS[inv.status ?? "pending_payment_instructions"] || "bg-gray-100 text-gray-700"}`} data-testid={`badge-invoice-status-${inv.id}`}>
                        {INVOICE_STATUS_LABELS[inv.status ?? ""] || inv.status}
                      </span>
                      {inv.status === "paid" && (
                        <div className="text-xs text-gray-500 mt-0.5" data-testid={`text-paid-on-${inv.id}`}>
                          {inv.paymentDate
                            ? `Paid ${new Date(inv.paymentDate as unknown as string).toLocaleDateString()}`
                            : inv.paidAt
                              ? `Paid ${new Date(inv.paidAt as unknown as string).toLocaleDateString()}`
                              : "—"}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-700" data-testid={`text-reminder-count-${inv.id}`}>
                      {(inv.reminderCount ?? 0) > 0 ? (
                        <span title={inv.lastReminderSentAt ? `Last sent ${new Date(inv.lastReminderSentAt).toLocaleString()}` : undefined}>
                          {inv.reminderCount}
                          {inv.lastReminderSentAt && (
                            <span className="text-gray-400 ml-1">· {new Date(inv.lastReminderSentAt).toLocaleDateString()}</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        {inv.status === "pending_payment_instructions" && (
                          <Button size="sm" className="h-7 text-xs" onClick={() => sendMutation.mutate(inv.id)} disabled={sendMutation.isPending || !paymentInstructionsConfigured} title={!paymentInstructionsConfigured ? "Configure payment instructions first (Settings → Payment Instructions)" : undefined} data-testid={`button-send-invoice-${inv.id}`}>
                            <Send className="h-3.5 w-3.5 mr-1" /> Send
                          </Button>
                        )}
                        {inv.status === "disputed" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => approveMutation.mutate(inv.id)} disabled={approveMutation.isPending} data-testid={`button-approve-invoice-${inv.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-green-600" /> Approve
                          </Button>
                        )}
                        {["sent", "overdue", "disputed"].includes(inv.status ?? "") && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => sendMutation.mutate(inv.id)} disabled={sendMutation.isPending} data-testid={`button-resend-invoice-${inv.id}`}>
                            <Send className="h-3.5 w-3.5 mr-1" /> Resend
                          </Button>
                        )}
                        {["sent", "overdue", "disputed"].includes(inv.status ?? "") && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openMarkPaid(inv)} data-testid={`button-mark-paid-${inv.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Mark Paid
                          </Button>
                        )}
                        {(() => {
                          const sentish = ["sent", "overdue"].includes(inv.status ?? "");
                          const isOverdue = !!inv.dueDate && new Date(inv.dueDate) < new Date();
                          if (!sentish || !isOverdue) return null;
                          const isPending = reminderMutation.isPending && reminderMutation.variables === inv.id;
                          return (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50"
                              onClick={() => reminderMutation.mutate(inv.id)}
                              disabled={isPending}
                              data-testid={`button-send-reminder-invoice-${inv.id}`}
                            >
                              {isPending ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              ) : (
                                <BellRing className="h-3.5 w-3.5 mr-1" />
                              )}
                              Send reminder
                            </Button>
                          );
                        })()}
                        {inv.pdfDataUrl && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" asChild data-testid={`button-pdf-invoice-${inv.id}`}>
                            <a href={`/api/admin/referrals/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer"><Eye className="h-3.5 w-3.5" /></a>
                          </Button>
                        )}
                        {!["paid", "voided"].includes(inv.status ?? "") && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => voidMutation.mutate(inv.id)} data-testid={`button-void-invoice-${inv.id}`}>
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Dialog open={!!markPaidInvoice} onOpenChange={(open) => { if (!open) setMarkPaidInvoice(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Invoice Paid</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-gray-600">
              Confirm payment for invoice <span className="font-mono">{markPaidInvoice?.invoiceNumber}</span>.
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="payment-date">Payment date</Label>
              <Input
                id="payment-date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                data-testid="input-payment-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidInvoice(null)} data-testid="button-cancel-mark-paid">Cancel</Button>
            <Button
              onClick={() => markPaidInvoice && markPaidMutation.mutate({ id: markPaidInvoice.id, paymentDate })}
              disabled={!paymentDate || markPaidMutation.isPending}
              data-testid="button-confirm-mark-paid"
            >
              {markPaidMutation.isPending ? "Saving…" : "Confirm Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
